import { CopyOutlined, QuestionCircleOutlined, SyncOutlined } from "@ant-design/icons";
import { Button, Checkbox, Col, Flex, Form, Input, Row, Select } from "antd";
import TextArea from "antd/es/input/TextArea";
import { TEMP_USER_STATE } from "components/Forms/UserDetailEditForm/formSchema.ts";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { CountrySelectSearchable } from "components/UI/Selects/CountrySelectSearchable/CountrySelectSearchable.tsx";
import { CurrencySelectSearchable } from "components/UI/Selects/CurrencySelectSearchable/CurrencySelectSearchable.tsx";
import { LanguageSelectSearchable } from "components/UI/Selects/LanguageSelectSearchable/LanguageSelectSearchable.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { FC, Fragment, useEffect, useMemo, useState } from "react";
import { FormItem } from "react-hook-form-antd";
import { FormId } from "types/models";

import { AddUserFormData, buildAddUserFormSchema } from "./formSchema";

interface AddUserFormProps {
    handleSubmit: (formData: AddUserFormData) => void | Promise<void>;
    showPasswordFields: boolean;
    initialValues?: Partial<AddUserFormData>;
    disabledFields?: Array<keyof AddUserFormData>;
    preserveSubmittedValuesOnReset?: boolean;
}

interface AddUserFormContentProps {
    showPasswordFields: boolean;
    disabledFields: Set<keyof AddUserFormData>;
}

const AddUserFormContent: FC<AddUserFormContentProps> = ({ showPasswordFields, disabledFields }) => {
    const { control, fieldNames } = useGeneralForm<AddUserFormData>();
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tDetail } = usePaldockTranslation("forms.user-detail-edit");
    const { t: tCommon } = usePaldockTranslation("common");

    return (
        <Fragment>
            {/*
             * Dummy/placeholder fields ahead of the future "global fields" redesign, alongside the
             * real ones above — not wired to any data yet, kept disabled on purpose. See
             * UserDetailEditForm, which has the same set but isn't mounted anywhere.
             */}
            <DrawerCollapsible title={tDetail("sections.user")}>
                <Row gutter={16}>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.nickname.name}
                            label={tFields("nickname.label")}
                            required
                        >
                            <Input
                                placeholder={tFields("nickname.placeholder")}
                                disabled={disabledFields.has("nickname")}
                            />
                        </FormItem>
                    </Col>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.email.name}
                            label={tFields("email.label")}
                            required
                        >
                            <Input
                                type="email"
                                placeholder={tFields("email.placeholder")}
                                disabled={disabledFields.has("email")}
                            />
                        </FormItem>
                    </Col>
                    <Col span={8}>
                        <Form.Item label={tDetail("fields.phone.label")}>
                            <Input placeholder={tDetail("fields.phone.placeholder")} disabled />
                        </Form.Item>
                    </Col>
                </Row>
                {showPasswordFields && (
                    <Row gutter={16}>
                        <Col span={8}>
                            <FormItem
                                control={control}
                                name={fieldNames.password?.name || "password"}
                                label={tFields("password.label")}
                            >
                                <Input.Password disabled={disabledFields.has("password")} />
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem
                                control={control}
                                name={fieldNames.password_confirmation?.name || "password_confirmation"}
                                label={tFields("password_confirmation.label")}
                            >
                                <Input.Password disabled={disabledFields.has("password_confirmation")} />
                            </FormItem>
                        </Col>
                        <Col span={8} />
                    </Row>
                )}
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label={tFields("name.label")}>
                            <Input placeholder={tFields("name.placeholder")} disabled />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label={tDetail("fields.surname.label")}>
                            <Input placeholder={tDetail("fields.surname.placeholder")} disabled />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item label={tDetail("fields.websites.label")}>
                            <Select
                                className="!h-8 w-full"
                                placeholder={tDetail("fields.websites.placeholder")}
                                disabled
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={24} className="mb-4">
                        <Checkbox disabled>{tDetail("fields.send_invitation.label")}</Checkbox>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={8}>
                        <Button type="link" disabled>
                            {tDetail("actions.reset_password")}
                        </Button>
                    </Col>
                </Row>
            </DrawerCollapsible>
            {/* <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Form.Item
                        name="terms_agreement"
                        valuePropName="checked"
                        rules={[{ required: true, message: t("messages.must_agree_terms") }]}
                    >
                        <Checkbox>
                            <Trans
                                i18nKey="forms.add-user.terms"
                                components={{
                                    a: (
                                        <a
                                            href="https://paldock.fra1.cdn.digitaloceanspaces.com/Leadgenia.com%20VOP.pdf"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        />
                                    ),
                                }}
                            />
                        </Checkbox>
                    </Form.Item>
                </Col>
            </Row> */}
            <DrawerCollapsible title={tCommon("settings")}>
                <Row gutter={16}>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.country_id?.name || "country_id"}
                            label={tFields("country.label")}
                        >
                            <CountrySelectSearchable
                                placeholder={tFields("country.placeholder")}
                                disabled={disabledFields.has("country_id")}
                            />
                        </FormItem>
                    </Col>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.language_id?.name || "language_id"}
                            label={tDetail("fields.language.label")}
                        >
                            <LanguageSelectSearchable
                                placeholder={tDetail("fields.language.placeholder")}
                                disabled={disabledFields.has("language_id")}
                            />
                        </FormItem>
                    </Col>
                    <Col span={8}>
                        <FormItem
                            control={control}
                            name={fieldNames.currency_id?.name || "currency_id"}
                            label={tFields("currency.label")}
                        >
                            <CurrencySelectSearchable
                                placeholder={tFields("currency.placeholder")}
                                disabled={disabledFields.has("currency_id")}
                            />
                        </FormItem>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item label={tDetail("fields.user_id.label")}>
                            <Input placeholder={tDetail("fields.user_id.placeholder")} disabled />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label={tFields("state.label")}>
                            <Select className="!h-8 w-full" placeholder={tFields("state.placeholder")} disabled>
                                {Object.values(TEMP_USER_STATE).map((state) => (
                                    <Select.Option key={state} value={state} title={state}>
                                        {state}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label={tDetail("fields.manager.label")}>
                            <Select
                                className="!h-8 w-full"
                                placeholder={tDetail("fields.manager.placeholder")}
                                disabled
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item
                    label={
                        <Flex align="center" gap="small">
                            {tDetail("fields.api_token.label")} <QuestionCircleOutlined />
                        </Flex>
                    }
                >
                    <Flex gap="small" align="center">
                        <Input placeholder={tDetail("fields.api_token.placeholder")} disabled />
                        <Button size="middle" type="primary" icon={<SyncOutlined />} disabled>
                            {tDetail("actions.generate")}
                        </Button>
                        <Button size="middle" type="primary" icon={<CopyOutlined />} disabled>
                            {tCommon("copy")}
                        </Button>
                    </Flex>
                </Form.Item>
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item label={tDetail("fields.note.label")}>
                            <TextArea rows={4} placeholder={tDetail("fields.note.placeholder")} disabled />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label={tFields("category.label")}>
                            <Select className="!h-8 w-full" placeholder={tFields("category.placeholder")} disabled />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label={tDetail("fields.labels.label")}>
                            <Select
                                className="!h-8 w-full"
                                placeholder={tDetail("fields.labels.placeholder")}
                                disabled
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <Form.Item label={tDetail("fields.permitted_private_offers.label")}>
                            <Select
                                className="!h-8 w-full"
                                placeholder={tDetail("fields.permitted_private_offers.placeholder")}
                                disabled
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </DrawerCollapsible>
        </Fragment>
    );
};

const AddUserFormSync: FC<{ defaultValues: AddUserFormData }> = ({ defaultValues }) => {
    const { reset } = useGeneralForm<AddUserFormData>();

    useEffect(() => {
        reset(defaultValues);
    }, [defaultValues, reset]);

    return null;
};

export const AddUserForm: FC<AddUserFormProps> = (props) => {
    const disabledFields = new Set(props.disabledFields || []);
    const schema = buildAddUserFormSchema(props.showPasswordFields);
    const defaultValues = useMemo<AddUserFormData>(
        () => ({
            nickname: "",
            email: "",
            password: "",
            password_confirmation: "",
            country_id: undefined,
            language_id: undefined,
            currency_id: undefined,
            ...props.initialValues,
        }),
        [props.initialValues]
    );
    const [syncedDefaultValues, setSyncedDefaultValues] = useState(defaultValues);

    useEffect(() => {
        setSyncedDefaultValues(defaultValues);
    }, [defaultValues]);

    return (
        <GeneralFormProvider<AddUserFormData>
            id={FormId.AddUserForm}
            schema={schema}
            defaultValues={syncedDefaultValues}
            resetOnSubmit={false}
            onSubmit={async (data) => {
                try {
                    await props.handleSubmit(data);
                } catch {
                    // Rejected submit (e.g. BE validation) — keep the user's input, error is reported by the caller.
                    return;
                }

                // Only after a successful submit — AddUserFormSync resets the form to the new defaults.
                setSyncedDefaultValues(props.preserveSubmittedValuesOnReset ? data : { ...defaultValues });
            }}
        >
            <AddUserFormSync defaultValues={syncedDefaultValues} />
            <AddUserFormContent showPasswordFields={props.showPasswordFields} disabledFields={disabledFields} />
        </GeneralFormProvider>
    );
};
